import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements
} from '@stripe/react-stripe-js';
import './Checkout.css';
import API_BASE_URL from '../config/api';

const STRIPE_PK = 'pk_live_51TGcfPKmhnqdWeH5Q4hXyWUSydkJdPMJwDd4PR1DsAW2hSlDYJmstsZLprRCyUIxd6a0j3uLLTDNhkMfC1BEw3Fk002NxTskSi'
const stripePromise = loadStripe(STRIPE_PK);

// ─── Inner checkout form (needs Elements context) ─────────────────────────────

const paymentElementOptions = {
    layout: {
        type: 'tabs',
        defaultCollapsed: false,
    }
};

function CheckoutForm({
    amount,
    description,
    returnUrl,
    onCancel,
    type,
    profiles,
    buyerEmail,
    buyerName,
    buyerUserId,
    eventId,
    periodMonth,
    periodYear,
    overdueMonths,
    selectedUserId
}) {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [isReady, setIsReady] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setIsProcessing(true);
        setErrorMessage(null);

        // 1. Submit the elements to trigger validation
        const { error: submitError } = await elements.submit();
        if (submitError) {
            setErrorMessage(submitError.message);
            setIsProcessing(false);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            let clientSecret;

            // 2. Create the Payment Intent on the server ONLY at the exact moment of payment
            if (type === 'ticket') {
                const res = await fetch(`${API_BASE_URL}/api/Stripe/create-payment-intent`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        eventId: eventId?.toString() || '',
                        buyerUserId: buyerUserId || null,
                        buyerEmail: buyerEmail || '',
                        buyerName: buyerName || '',
                        amount,
                        profiles: profiles?.map(p => ({
                            id: p.id,
                            name: p.name,
                            email: p.email,
                            price: p.price,
                            sportId: p.sportId || null,
                        })) || [],
                    })
                });

                if (!res.ok) throw new Error((await res.json()).message || 'Erro ao criar sessão.');
                const data = await res.json();
                clientSecret = data.clientSecret;

            } else if (type === 'quota') {
                const res = await fetch(`${API_BASE_URL}/api/payment/payment-intent`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        userId: selectedUserId || null,
                        periods: (() => {

                            const allPeriods = overdueMonths?.length > 0
                                ? overdueMonths.map(m => ({ month: m.periodMonth ?? null, year: m.periodYear }))
                                : [];

                            if (periodYear) {
                                const alreadyIncluded = allPeriods.some(
                                    p => (p.month ?? null) === (periodMonth ?? null) && p.year === periodYear
                                );
                                if (!alreadyIncluded) {
                                    allPeriods.push({ month: periodMonth ?? null, year: periodYear });
                                }
                            }

                            return allPeriods.length > 0
                                ? allPeriods
                                : [{ month: periodMonth ?? null, year: periodYear }];
                        })()
                    })
                });

                if (!res.ok) throw new Error((await res.json()).message || 'Erro ao criar sessão.');
                const data = await res.json();
                clientSecret = data.clientSecret;
            }

            const { error } = await stripe.confirmPayment({
                elements,
                clientSecret,
                confirmParams: {
                    return_url: returnUrl,
                },
            });

            if (error) {
                setErrorMessage(error.message);
                setIsProcessing(false);
            }
        } catch (err) {
            setErrorMessage(err.message || 'Ocorreu um erro ao processar o pagamento.');
            setIsProcessing(false);
        }
    };

    return (
        <form id="checkout-payment-form" onSubmit={handleSubmit}>
            <div className="co-payment-element-wrapper">
                <PaymentElement
                    id="payment-element"
                    onReady={() => setIsReady(true)}
                    options={paymentElementOptions}
                />
            </div>

            {errorMessage && (
                <div className="co-error-banner" role="alert">
                    <i className="fas fa-exclamation-triangle" />
                    <span>{errorMessage}</span>
                </div>
            )}

            <div className="co-actions">
                <button
                    type="button"
                    className="co-btn-cancel"
                    onClick={onCancel}
                    disabled={isProcessing}
                >
                    Cancelar
                </button>
                <button
                    id="checkout-submit-btn"
                    type="submit"
                    className="co-btn-pay"
                    disabled={!stripe || !elements || isProcessing || !isReady}
                >
                    {isProcessing ? (
                        <>
                            <span className="co-spinner" />
                            A processar...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-lock" />
                            Pagar {amount?.toFixed(2)}€
                        </>
                    )}
                </button>
            </div>

            <p className="co-secure-note">
                Pagamento seguro processado pelo Stripe
            </p>
        </form>
    );
}


export default function Checkout() {
    const location = useLocation();
    const navigate = useNavigate();

    const {
        type,
        amount,
        description,
        eventId,
        profiles,
        buyerEmail,
        buyerName,
        buyerUserId,
        successPath,
        cancelPath,
        queryParams,
        periodMonth,
        periodYear,
        overdueMonths,
        breakdown,
        inscriptionInfo,
        currentMonthDetails,
        selectedUserId,
        isAnnual,
    } = location.state || {};

    const [extraQuotaData, setExtraQuotaData] = useState(null);

    useEffect(() => {
        if (!type || !amount) {
            navigate('/', { replace: true });
            return;
        }

        if (type === 'quota') {
            const token = localStorage.getItem('token');
            if (token) {
                fetch(`${API_BASE_URL}/api/payment/quota?userId=${selectedUserId || ''}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                    .then(res => res.ok ? res.json() : null)
                    .then(data => {
                        if (data) setExtraQuotaData(data);
                    })
                    .catch(() => { });
            }
        }
    }, [type, amount, selectedUserId, navigate]);

    const origin = window.location.origin;
    const successBase = successPath || (type === 'ticket' ? '/bilheteria' : '/dashboard-atleta');
    const successQuery = queryParams || (type === 'quota' ? '?payment=success' : '?success=true');
    const returnUrl = `${origin}${successBase}${successQuery}`;

    const handleCancel = () => {
        const cancelTarget = cancelPath || (type === 'ticket' ? '/bilheteria' : -1);
        if (typeof cancelTarget === 'number') navigate(cancelTarget);
        else navigate(cancelTarget);
    };

    const getMonthSubItems = (item) => {
        const subItems = [];

        // Determine if this specific month includes inscriptions in its billed amount
        const hasInscriptions = (item.inscriptionsAmount != null && item.inscriptionsAmount > 0)
            || (Array.isArray(item.inscriptions) && item.inscriptions.length > 0)
            || (item.monthlyQuota != null && item.amount > item.monthlyQuota);

        // 1. If explicit breakdown array is available
        if (Array.isArray(item.breakdown) && item.breakdown.length > 0) {
            item.breakdown.forEach(b => {
                const rawLabel = b.label || '';
                const lower = rawLabel.toLowerCase();
                let label = rawLabel;
                let icon = 'fas fa-chevron-right';
                let itemType = 'item';

                const isInscription = lower.includes('inscrição') || lower.includes('inscricao');

                // If this month does not charge inscriptions, skip any inscription items
                if (isInscription && !hasInscriptions) {
                    return;
                }

                if (isInscription) {
                    icon = 'fas fa-file-signature';
                    itemType = 'inscription';
                    label = rawLabel;
                } else if (lower.includes('quota')) {
                    icon = 'fas fa-id-card';
                    itemType = 'quota';
                    label = rawLabel;
                } else if (b.sportName || lower.includes('mensalidade') || lower.includes('sub-') || lower.includes('mini') || lower.includes('senior') || lower.includes('veterano')) {
                    icon = 'fas fa-running';
                    itemType = 'modality';
                    if (!lower.includes('mensalidade')) {
                        label = `Mensalidade — ${rawLabel}`;
                    }
                } else if (lower.includes('jóia') || lower.includes('joia')) {
                    icon = 'fas fa-gem';
                    itemType = 'admission';
                }

                subItems.push({
                    label,
                    amount: b.amount,
                    isZero: b.amount === 0,
                    isDiscount: b.isDiscount,
                    icon,
                    type: itemType,
                });
            });

            // Check if there are any extra unpaid inscriptions in inscriptionInfo not yet listed in breakdown
            if (hasInscriptions && Array.isArray(item.inscriptionInfo)) {
                item.inscriptionInfo.forEach(ins => {
                    if (!ins.paid && ins.feeDiscount > 0) {
                        const alreadyPresent = subItems.some(
                            s => s.type === 'inscription' && s.label.toLowerCase().includes(ins.sportName?.toLowerCase() || '')
                        );
                        if (!alreadyPresent) {
                            subItems.push({
                                label: `Inscrição — ${ins.sportName}${ins.escalao ? ` (${ins.escalao})` : ''}`,
                                amount: ins.feeDiscount,
                                isZero: false,
                                icon: 'fas fa-file-signature',
                                type: 'inscription'
                            });
                        }
                    }
                });
            }
        } else {
            // 2. Fallback if breakdown array wasn't provided
            const inscList = item.inscriptions || [];
            const hasInsc = hasInscriptions || inscList.length > 0 || (item.inscriptionsAmount && item.inscriptionsAmount > 0);

            if (hasInsc) {
                const quotaModalityAmt = item.monthlyQuota != null
                    ? item.monthlyQuota
                    : (item.amount - (item.inscriptionsAmount || 0));

                if (quotaModalityAmt > 0) {
                    subItems.push({
                        label: 'Mensalidade / Quota',
                        amount: quotaModalityAmt,
                        isZero: false,
                        icon: 'fas fa-id-card',
                        type: 'quota'
                    });
                }

                if (inscList.length > 0) {
                    inscList.forEach(insc => {
                        subItems.push({
                            label: `Inscrição — ${insc.sportName || 'Modalidade'}`,
                            amount: insc.amount,
                            isZero: false,
                            icon: 'fas fa-file-signature',
                            type: 'inscription'
                        });
                    });
                } else if (item.inscriptionsAmount > 0) {
                    subItems.push({
                        label: 'Taxa de Inscrição',
                        amount: item.inscriptionsAmount,
                        isZero: false,
                        icon: 'fas fa-file-signature',
                        type: 'inscription'
                    });
                }
            } else {
                subItems.push({
                    label: 'Quota de Sócio',
                    amount: item.amount,
                    isZero: item.amount === 0,
                    icon: 'fas fa-id-card',
                    type: 'quota'
                });
            }
        }

        return subItems;
    };

    const elementsOptions = React.useMemo(() => {
        if (!amount) return null;
        return {
            mode: 'payment',
            amount: Math.round(amount * 100),
            currency: 'eur',
            appearance: {
                theme: 'stripe',
                variables: {
                    colorPrimary: '#003380',
                    colorBackground: '#ffffff',
                    colorText: '#0f172a',
                    colorDanger: '#dc2626',
                    fontFamily: '"Barlow", system-ui, sans-serif',
                    borderRadius: '6px',
                }
            },
            locale: 'pt',
            defaultValues: {
                billingDetails: {
                    email: buyerEmail || '',
                    name: buyerName || '',
                }
            }
        };
    }, [amount, buyerEmail, buyerName]);

    return (
        <div className="co-page">
            <aside className="co-sidebar">
                <div className="co-sidebar-inner">
                    <div className="co-brand">
                        <img src="/CDP_logo.png" alt="CDP Logo" className="co-logo" />
                        <span className="co-club-name">Clube Desportivo da Póvoa</span>
                    </div>

                    <div className="co-divider" />

                    <div className="co-order-summary">
                        <p className="co-order-label">Resumo do pedido</p>

                        <div className="co-order-item">
                            <div className="co-order-item-icon">
                                <i className={type === 'quota' ? 'fas fa-file-invoice-dollar' : 'fas fa-ticket'} />
                            </div>
                            <div className="co-order-item-info">
                                <span className="co-order-item-name">
                                    {(description || 'Pagamento')
                                        .replace(/quotas em atraso/gi, 'Quotas')
                                        .replace(/quota em atraso/gi, 'Quota')
                                        .replace(/quotas em falta/gi, 'Quotas')
                                        .replace(/quota em falta/gi, 'Quota')}
                                </span>
                                <span className="co-order-item-sub">
                                    {type === 'quota' ? 'Quota de sócio' : 'Bilhete de evento'}
                                </span>
                            </div>
                        </div>

                        {type === 'quota' && (() => {
                            const items = [];

                            if (overdueMonths?.length > 0) {
                                overdueMonths.forEach((m) => {
                                    const matchedOverdue = extraQuotaData?.overdueMonths?.find(
                                        om => om.periodMonth === m.periodMonth && om.periodYear === m.periodYear
                                    );
                                    const matchedFuture = extraQuotaData?.futureMonths?.find(
                                        fm => fm.periodMonth === m.periodMonth && fm.periodYear === m.periodYear
                                    );

                                    items.push({
                                        month: m.periodMonth,
                                        year: m.periodYear,
                                        amount: m.amount,
                                        monthlyQuota: m.monthlyQuota ?? matchedOverdue?.monthlyQuota ?? matchedFuture?.monthlyQuota,
                                        breakdown: m.breakdown ?? matchedOverdue?.breakdown ?? matchedFuture?.breakdown,
                                        inscriptionInfo: m.inscriptionInfo ?? matchedOverdue?.inscriptionInfo ?? matchedFuture?.inscriptionInfo,
                                        inscriptions: m.inscriptions ?? matchedFuture?.inscriptions,
                                        inscriptionsAmount: m.inscriptionsAmount ?? matchedFuture?.inscriptionsAmount,
                                        isCurrent: false,
                                    });
                                });
                            }

                            if (periodMonth && periodYear) {
                                const alreadyIn = items.some(
                                    p => p.month === periodMonth && p.year === periodYear
                                );
                                if (!alreadyIn) {
                                    const overdueTotal = overdueMonths?.reduce((s, m) => s + (m.amount || 0), 0) || 0;
                                    const currentMonthAmount = amount != null ? (amount - overdueTotal) : null;
                                    items.push({
                                        month: periodMonth,
                                        year: periodYear,
                                        amount: currentMonthAmount != null && currentMonthAmount > 0 ? currentMonthAmount : amount,
                                        monthlyQuota: currentMonthDetails?.monthlyQuota ?? extraQuotaData?.monthlyQuota ?? (amount != null ? (amount - overdueTotal) : null),
                                        breakdown: currentMonthDetails?.breakdown ?? breakdown ?? extraQuotaData?.breakdown,
                                        inscriptionInfo: currentMonthDetails?.inscriptionInfo ?? inscriptionInfo ?? extraQuotaData?.inscriptionInfo,
                                        inscriptions: currentMonthDetails?.inscriptions,
                                        inscriptionsAmount: currentMonthDetails?.inscriptionsAmount,
                                        isCurrent: true,
                                    });
                                }
                            }

                            // Sort chronologically
                            items.sort((a, b) => {
                                const valA = (a.year || 0) * 12 + (a.month || 0);
                                const valB = (b.year || 0) * 12 + (b.month || 0);
                                return valA - valB;
                            });

                            if (items.length === 0) return null;

                            return (
                                <div className="co-profiles-list">
                                    {items.map((item, idx) => {
                                        const isAnnualPayment = isAnnual || description?.toLowerCase().includes('anual');
                                        let periodLabel = '';
                                        if (isAnnualPayment && item.month && item.year) {
                                            const startMonthName = new Date(item.year, item.month - 1, 1).toLocaleDateString('pt-PT', { month: 'long' });
                                            const endMonth = item.month === 1 ? 12 : item.month - 1;
                                            const endYear = item.month === 1 ? item.year : item.year + 1;
                                            const endMonthName = new Date(endYear, endMonth - 1, 1).toLocaleDateString('pt-PT', { month: 'long' });
                                            periodLabel = `${startMonthName.charAt(0).toUpperCase() + startMonthName.slice(1)} ${item.year} - ${endMonthName.charAt(0).toUpperCase() + endMonthName.slice(1)} ${endYear}`;
                                        } else if (item.month && item.year) {
                                            const date = new Date(item.year, item.month - 1, 1);
                                            const monthName = date.toLocaleString('pt-PT', { month: 'long', year: 'numeric' });
                                            periodLabel = monthName.charAt(0).toUpperCase() + monthName.slice(1);
                                        } else {
                                            periodLabel = item.year ? `Quota ${item.year}` : 'Quota';
                                        }

                                        const subItems = getMonthSubItems(item);

                                        return (
                                            <div key={idx} className="co-month-card">
                                                <div className="co-month-card-header">
                                                    <span className="co-month-card-title">
                                                        {periodLabel}
                                                        {item.isCurrent && <span className="co-profile-current-badge">atual</span>}
                                                    </span>
                                                    <span className={`co-month-card-price ${!item.isCurrent ? 'co-month-card-price--overdue' : ''}`}>
                                                        {item.amount != null ? Number(item.amount).toFixed(2) : '0.00'}€
                                                    </span>
                                                </div>

                                                {subItems.length > 0 && (
                                                    <div className="co-month-subitems">
                                                        {subItems.map((sub, sIdx) => (
                                                            <div key={sIdx} className="co-month-subrow">
                                                                <span className="co-month-subrow-label">
                                                                    <i className={`${sub.icon} co-month-subrow-icon`} />
                                                                    <span>{sub.label}</span>
                                                                </span>
                                                                <span className={`co-month-subrow-price ${sub.isZero ? 'co-month-subrow-price--zero' : ''}`}>
                                                                    {sub.amount != null ? Number(sub.amount).toFixed(2) : '0.00'}€
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })()}

                        {profiles && profiles.length > 0 && (
                            <div className="co-profiles-list">
                                {profiles.map((p, i) => (
                                    <div key={i} className="co-profile-row">
                                        <span className="co-profile-name">{p.name}</span>
                                        <span className="co-profile-price">{p.price?.toFixed(2)}€</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="co-divider" />

                        <div className="co-total-row">
                            <span>Total</span>
                            <span className="co-total-amount">{amount?.toFixed(2)}€</span>
                        </div>
                    </div>

                    <div className="co-security-badges">
                        <div className="co-badge">
                            <span>Pagamento Seguro</span>
                        </div>
                        <div className="co-badge">
                            <i className="fab fa-stripe" />
                            <span>Processado pelo Stripe</span>
                        </div>
                    </div>
                </div>
            </aside>

            <main className="co-main">
                <div className="co-main-inner">
                    <button className="co-back-btn" onClick={handleCancel}>
                        <i className="fas fa-arrow-left" />
                        Voltar
                    </button>

                    <h1 className="co-title">Finalizar Pagamento</h1>
                    <p className="co-subtitle">Escolhe o método de pagamento</p>

                    {elementsOptions ? (
                        <Elements stripe={stripePromise} options={elementsOptions}>
                            <CheckoutForm
                                amount={amount}
                                description={description}
                                returnUrl={returnUrl}
                                onCancel={handleCancel}
                                type={type}
                                profiles={profiles}
                                buyerEmail={buyerEmail}
                                buyerName={buyerName}
                                buyerUserId={buyerUserId}
                                eventId={eventId}
                                periodMonth={periodMonth}
                                periodYear={periodYear}
                                overdueMonths={overdueMonths}
                                selectedUserId={selectedUserId}
                            />
                        </Elements>
                    ) : (
                        <div className="co-loading">
                            <div className="co-loading-spinner" />
                            <p>A carregar formulário de pagamento...</p>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
